import { Pagination } from '@mantine/core';
import { IconDownload } from '@tabler/icons-react';
import { formatBaht } from '@xver/utils/number';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { HistoryBillResponse } from '@xver/@types/bill';
import { DataTable, DataTableColumn } from '@xver/components/Organisms';
import i18n from '@xver/i18n';
import httpClient from '@xver/libs/axios';
import { formatDate } from '@xver/utils/date';

interface HistoryRow {
    id: number;
    createdAt: string;
    status: string;
    amount: number;
    file: string;
    chargeID: string;
    gateway: string;
    sourceID: string;
    updateAt: string;
}

interface BillingHistoryTableProps {
    response: HistoryBillResponse | undefined;
    isLoading?: boolean;
    page: number;
    onPageChange: (next: number) => void;
    billingAddressComplete: boolean;
}

export default function BillingHistoryTable({
    response,
    isLoading,
    page,
    onPageChange,
    billingAddressComplete,
}: BillingHistoryTableProps) {
    const { t, i18n: i18nInstance } = useTranslation('billings');
    const lang = i18nInstance.language;

    const columns = useMemo<DataTableColumn<HistoryRow>[]>(
        () => [
            {
                key: 'date',
                label: t('history.columns.date'),
                render: (row) => (
                    <span className="text-sm text-fg/85">
                        {formatDate(row.createdAt, lang, i18n.t.bind(i18n))}
                    </span>
                ),
            },
            {
                key: 'description',
                label: t('history.columns.description'),
                render: () => <span className="text-sm">{t('history.topUp')}</span>,
            },
            {
                key: 'amount',
                label: t('history.columns.amount'),
                render: (row) => (
                    <span className="text-sm font-medium">{formatBaht(row.amount)} ฿</span>
                ),
            },
            {
                key: 'status',
                label: t('history.columns.status'),
                render: (row) => <StatusPill status={row.status} />,
            },
            {
                key: 'receipt',
                label: t('history.columns.receipt'),
                align: 'right',
                render: (row) => {
                    if (row.status !== 'successful' || !row.file) {
                        return <span className="text-xs text-fg/68">—</span>;
                    }
                    if (!billingAddressComplete) {
                        return (
                            <span className="text-xs text-fg/68">
                                {t('history.downloadDisabled')}
                            </span>
                        );
                    }
                    return <ReceiptDownloadButton file={row.file} label={t('history.download')} />;
                },
            },
        ],
        [billingAddressComplete, t, lang]
    );

    return (
        <div className="w-full flex flex-col gap-3">
            <DataTable
                caption={t('history.title')}
                columns={columns}
                rows={response?.data as HistoryRow[] | undefined}
                isLoading={isLoading}
                emptyMessage={t('history.empty')}
                rowKey={(row) => row.id}
            />
            {response && response.page_count > 1 && (
                <div className="w-full flex justify-end pt-2">
                    <Pagination
                        total={response.page_count}
                        value={page}
                        onChange={onPageChange}
                        size="sm"
                    />
                </div>
            )}
        </div>
    );
}

function ReceiptDownloadButton({ file, label }: { file: string; label: string }) {
    const [busy, setBusy] = useState(false);

    const onClick = async (e: React.MouseEvent) => {
        e.preventDefault();
        if (busy) return;
        setBusy(true);
        try {
            const blob = await httpClient.get<Blob>(file, { responseType: 'blob' });
            const url = window.URL.createObjectURL(blob as unknown as Blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = file.split('/').pop() || 'receipt.pdf';
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } finally {
            setBusy(false);
        }
    };

    return (
        <button
            type="button"
            onClick={onClick}
            disabled={busy}
            className="inline-flex items-center gap-1 text-xs text-blue-300 light:text-blue-700 hover:text-blue-200 light:hover:text-blue-800 disabled:opacity-50"
        >
            <IconDownload size={14} />
            {label}
        </button>
    );
}

function StatusPill({ status }: { status: string }) {
    const { t } = useTranslation('billings');
    const color =
        status === 'successful'
            ? 'bg-green-900/40 text-green-300 border-green-800/60 light:bg-green-100 light:text-green-800 light:border-green-300'
            : status === 'pending'
              ? 'bg-yellow-900/40 text-yellow-200 border-yellow-800/60 light:bg-yellow-100 light:text-yellow-800 light:border-yellow-300'
              : 'bg-red-900/40 text-red-300 border-red-800/60 light:bg-red-100 light:text-red-700 light:border-red-300';
    const labelKey =
        status === 'successful' || status === 'pending'
            ? `history.status.${status}`
            : 'history.status.failed';
    return (
        <span
            className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded border-[0.5px] ${color}`}
        >
            {t(labelKey)}
        </span>
    );
}
